// Shared KPI tile (count + label + optional hint). Lives here so every feature
// (payroll, attendance, workload…) renders the same stat card; payroll keeps a
// re-export at its old path.
import React from 'react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

export interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'success' | 'warning' | 'info' | 'danger';
  /** While true, show a skeleton in place of the value (and suppress the hint)
   *  so the card never flashes a false "0"/"0 of 0" before the query resolves (US-22). */
  loading?: boolean;
}

// Theme tokens (not hardcoded hex) so the cards flip correctly in dark mode and match the
// rest of the app. `-soft` is the tinted surface; the DEFAULT is the icon/text colour.
const TONE: Record<NonNullable<StatCardProps['tone']>, string> = {
  default: 'text-charcoal bg-primary/10 [&_svg]:text-primary-500',
  success: 'text-charcoal bg-success-soft [&_svg]:text-success',
  warning: 'text-charcoal bg-warning-soft [&_svg]:text-warning',
  info: 'text-charcoal bg-info-soft [&_svg]:text-info',
  danger: 'text-charcoal bg-danger-soft [&_svg]:text-danger',
};

// `h-full` lets a grid/flex row of cards share one height: a card with a hint
// no longer sits taller than its neighbours (tiles stay aligned).
export const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, hint, tone = 'default', loading = false }) => (
  <Card className="h-full">
    <CardContent className="p-5 h-full">
      <div className="flex items-start gap-3">
        <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', TONE[tone])}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          {loading ? (
            <Skeleton className="h-7 w-16 mt-1" />
          ) : (
            <p className="text-h3 text-charcoal truncate" title={value}>{value}</p>
          )}
          {hint && !loading && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);
